/**
 * Dropdown — własny select z W PEŁNI stylowaną listą opcji (task #14, Faza 1).
 *
 * Zbudowany na Radix (`@radix-ui/react-select`), spójnie z `Tooltip` (Radix).
 * Powód powstania: `Select.tsx` to stylowany NATYWNY `<select>` — zamknięta
 * kontrolka jest nasza, ale ROZWINIĘTA lista opcji jest natywna (OS: kwadratowa,
 * systemowy niebieski highlight). `Dropdown` renderuje własny panel: zaokrąglony,
 * z tokenów semantycznych, hover/selected w kolorze AKCENTU (nie natywny niebieski),
 * scroll przy dużej liczbie opcji, portal (ucieka z `overflow:hidden`), pełna
 * obsługa klawiatury i ARIA „za darmo" od Radix.
 *
 * API celowo zbliżone do `Select` (`options`, `value`, `onChange`/`onValueChange`,
 * `placeholder`, `aria-label`, `disabled`, `data-testid`), żeby MIGRACJA (Faza 2)
 * była mechaniczna. NIE zastępuje `Select.tsx` — ten zostaje do czasu batcha.
 *
 * Uwaga Radix: `Select.Item` nie akceptuje `value=""` (pusty string rezerwowany na
 * „brak wyboru" → placeholder). Zewnętrznie API DOPUSZCZA `''` (spójnie z `Select`,
 * np. opcja „Wszystkie" w filtrach) — wewnątrz mapujemy `''` ↔ sentinel.
 */

import { forwardRef } from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';

import { cn } from '@/lib/utils';

export interface DropdownOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface DropdownOptionGroup {
  /** Nagłówek grupy (odpowiednik `<optgroup label>`). */
  label: string;
  options: DropdownOption[];
}

export interface DropdownProps {
  options: DropdownOption[];
  /**
   * Grupy opcji (odpowiednik `<optgroup>`) — renderowane PO płaskich `options`
   * (typowy układ: opcja pusta/„Wszystkie" płasko, potem grupy). Radix Select.Group
   * + Select.Label; nagłówek grupy nie jest klikalny.
   */
  groups?: DropdownOptionGroup[];
  value?: string;
  /** Alias `onValueChange` — obie nazwy działają (parytet z natywnym `Select` i Radix). */
  onChange?: (value: string) => void;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  /**
   * Tekst na ZWINIĘTEJ kontrolce, gdy ma być inny niż etykieta wybranej opcji. Domyślnie
   * (bez tego propa) Radix pokazuje na triggerze dokładnie `label` wybranej opcji — ten
   * prop pozwala pokazać skrót, a pełny opis zostawić na rozwiniętej liście (np. kolumna
   * tabeli, gdzie liczy się wąska komórka: trigger `6`, opcja „6 — Źródło…"). Pusty/pominięty
   * → zachowanie domyślne, bez zmiany dla istniejących wywołań.
   */
  triggerText?: string;
  disabled?: boolean;
  className?: string;
  /**
   * Dodatkowe klasy elementu z TEKSTEM wartości na zwiniętej kontrolce (nie całego triggera) —
   * np. wyrównanie wartości w komórce tabeli: `flex-1 text-right` (bez `flex-1` element zajmuje
   * tylko szerokość tekstu i `text-*` nic nie zmienia). Brak = wygląd bez zmian (tekst do lewej).
   */
  valueClassName?: string;
  id?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  /**
   * Pole wymagane — trafia na trigger jako `aria-required` (czytnik ekranu zapowiada „wymagane").
   * Tylko semantyka dla technologii wspierających: widoczne oznaczenie (gwiazdkę) rysuje
   * wołający przy etykiecie, a `Dropdown` nie jest kontrolką natywnego formularza, więc nie
   * ustawia `required` na ukrytym `<select>` Radixa.
   */
  'aria-required'?: boolean;
  /**
   * Id elementu opisującego kontrolkę (np. komunikat błędu pod polem) — trafia na trigger jako
   * `aria-describedby`, żeby czytnik ekranu odczytał ten opis razem z polem.
   */
  'aria-describedby'?: string;
  'data-testid'?: string;
}

/** Radix zabrania `value=""` na Item — mapujemy pusty string na sentinel. */
const EMPTY_SENTINEL = '__dropdown_empty__';
const toRadix = (v: string) => (v === '' ? EMPTY_SENTINEL : v);
const fromRadix = (v: string) => (v === EMPTY_SENTINEL ? '' : v);

/** Klasy pozycji listy — wspólne dla opcji płaskich i zgrupowanych. */
const ITEM_CLASS = cn(
  'relative flex cursor-pointer select-none items-center whitespace-nowrap rounded-md py-1.5 pl-2.5 pr-8 text-sm text-text-primary outline-none',
  'data-[highlighted]:bg-accent-500/15 data-[highlighted]:text-text-primary',
  'data-[state=checked]:font-medium data-[state=checked]:text-accent-600',
  'data-[disabled]:cursor-not-allowed data-[disabled]:text-text-muted data-[disabled]:opacity-60',
);

function DropdownItem({ opt }: { opt: DropdownOption }) {
  return (
    <SelectPrimitive.Item value={toRadix(opt.value)} disabled={opt.disabled} className={ITEM_CLASS}>
      <SelectPrimitive.ItemText>{opt.label}</SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator className="absolute right-2 inline-flex items-center">
        <Check className="h-4 w-4 text-accent-600" aria-hidden="true" />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
}

export const Dropdown = forwardRef<HTMLButtonElement, DropdownProps>(function Dropdown(
  {
    options,
    groups,
    value,
    onChange,
    onValueChange,
    placeholder,
    triggerText,
    disabled,
    className,
    valueClassName,
    id,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-required': ariaRequired,
    'aria-describedby': ariaDescribedBy,
    'data-testid': dataTestId,
  },
  ref,
) {
  const handleChange = (next: string) => {
    const mapped = fromRadix(next);
    onChange?.(mapped);
    onValueChange?.(mapped);
  };

  // `undefined` → Radix pokazuje placeholder; pusty string mapujemy na sentinel,
  // żeby dopasować się do opcji „Wszystkie" (value: '').
  const radixValue = value === undefined ? undefined : toRadix(value);

  return (
    <SelectPrimitive.Root value={radixValue} onValueChange={handleChange} disabled={disabled}>
      <SelectPrimitive.Trigger
        ref={ref}
        id={id}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-required={ariaRequired}
        aria-describedby={ariaDescribedBy}
        data-testid={dataTestId}
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded-lg border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-primary transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent',
          'data-[state=open]:ring-2 data-[state=open]:ring-accent-500 data-[state=open]:border-transparent',
          'data-[disabled]:cursor-not-allowed data-[disabled]:bg-bg-tertiary data-[disabled]:text-text-muted',
          'data-[placeholder]:text-text-muted',
          className,
        )}
      >
        <span className={cn('truncate text-left', valueClassName)}>
          {/* `triggerText` podany → renderujemy go jako children `Value`; Radix wtedy NIE
              podstawia etykiety wybranej opcji (patrz JSDoc propa). */}
          <SelectPrimitive.Value placeholder={placeholder}>
            {triggerText ? triggerText : undefined}
          </SelectPrimitive.Value>
        </span>
        <SelectPrimitive.Icon asChild>
          <ChevronDown className="h-4 w-4 flex-shrink-0 text-text-muted" aria-hidden="true" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          className={cn(
            // Panel co najmniej 11rem szeroki NIEZALEŻNIE od (potencjalnie wąskiego,
            // ściśniętego) triggera — inaczej w wąskim kontenerze lista opcji jest
            // przycięta/zawinięta. Nie przekracza viewportu. Radix popper ogarnia kolizje.
            'z-[10001] max-h-[min(20rem,var(--radix-select-content-available-height))] min-w-[max(11rem,var(--radix-select-trigger-width))] max-w-[calc(100vw-1rem)] overflow-hidden rounded-lg border border-border-primary bg-bg-elevated shadow-lg',
          )}
        >
          <SelectPrimitive.ScrollUpButton className="flex h-6 items-center justify-center bg-bg-elevated text-text-muted">
            <ChevronDown className="h-4 w-4 rotate-180" aria-hidden="true" />
          </SelectPrimitive.ScrollUpButton>

          <SelectPrimitive.Viewport className="p-1">
            {options.map((opt) => (
              <DropdownItem key={opt.value} opt={opt} />
            ))}
            {groups?.map((group) => (
              <SelectPrimitive.Group key={group.label}>
                <SelectPrimitive.Label className="px-2.5 py-1.5 text-xs font-semibold uppercase tracking-wide text-text-muted">
                  {group.label}
                </SelectPrimitive.Label>
                {group.options.map((opt) => (
                  <DropdownItem key={opt.value} opt={opt} />
                ))}
              </SelectPrimitive.Group>
            ))}
          </SelectPrimitive.Viewport>

          <SelectPrimitive.ScrollDownButton className="flex h-6 items-center justify-center bg-bg-elevated text-text-muted">
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          </SelectPrimitive.ScrollDownButton>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
});
